import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  Anchor, 
  Plus, 
  Search, 
  Users, 
  Bed, 
  Zap, 
  Grid3X3, 
  List, 
  Map,
  Euro,
  Star,
  Calendar,
  Settings,
  Eye,
  MapPin
} from 'lucide-react';

const YachtsManagement = () => {
  const [activeView, setActiveView] = useState('list'); // Changé de 'grid' à 'list' par défaut

  // Sample yacht data
  const yachtsData = [
    {
      id: 1,
      name: 'M/Y Serenity',
      builder: 'Benetti',
      year: 2019,
      length: '75m',
      type: 'Motor Yacht',
      flag: '🇺🇸',
      status: 'Disponible',
      guests: 12,
      cabins: 6,
      crew: 18,
      speed: '15kn',
      pricing: {
        high: '€850,000',
        low: '€650,000'
      },
      metrics: {
        charter: '78%',
        rating: '4.9',
        revenue: '€12M'
      },
      location: 'Monaco',
      image: 'serenity',
      source: 'Yacht Charter Index'
    },
    {
      id: 2,
      name: 'S/Y Wind Spirit',
      builder: 'Perini Navi',
      year: 2021,
      length: '56m',
      type: 'Sailing Yacht',
      flag: '🇩🇪',
      status: 'Disponible',
      guests: 10,
      cabins: 5,
      crew: 9,
      speed: '12kn',
      pricing: {
        high: '€420,000',
        low: '€320,000'
      },
      metrics: {
        charter: '85%',
        rating: '5.0',
        revenue: '€6.2M'
      },
      location: 'Palma',
      image: 'windspirit',
      source: 'Yacht Portfolio'
    },
    {
      id: 3,
      name: 'M/Y Ocean Dream',
      builder: 'Lurssen',
      year: 2020,
      length: '88m',
      type: 'Motor Yacht',
      flag: '🇬🇧',
      status: 'En Charter',
      guests: 16,
      cabins: 8,
      crew: 22,
      speed: '18kn',
      pricing: {
        high: '€1,200,000',
        low: '€950,000'
      },
      metrics: {
        charter: '92%',
        rating: '4.8',
        revenue: '€18.5M'
      },
      location: 'Antibes',
      image: 'oceandream',
      source: 'Direct'
    },
    {
      id: 4,
      name: 'M/Y Azure Wave',
      builder: 'Feadship',
      year: 2018,
      length: '65m',
      type: 'Motor Yacht',
      flag: '🇳🇱',
      status: 'Maintenance',
      guests: 12,
      cabins: 6,
      crew: 15,
      speed: '16kn',
      pricing: {
        high: '€680,000',
        low: '€520,000'
      },
      metrics: {
        charter: '74%',
        rating: '4.7',
        revenue: '€8.9M'
      },
      location: 'La Ciotat',
      image: 'azurewave',
      source: 'Fraser Yachts'
    },
    {
      id: 5,
      name: 'M/Y Golden Star',
      builder: 'Sunseeker',
      year: 2022,
      length: '42m',
      type: 'Motor Yacht',
      flag: '🇮🇹',
      status: 'Disponible',
      guests: 8,
      cabins: 4,
      crew: 10,
      speed: '20kn',
      pricing: {
        high: '€320,000',
        low: '€240,000'
      },
      metrics: {
        charter: '68%',
        rating: '4.6',
        revenue: '€4.8M'
      },
      location: 'Porto Cervo',
      image: 'goldenstar',
      source: 'Burgess'
    },
    {
      id: 6,
      name: 'S/Y Mistral',
      builder: 'Wally',
      year: 2023,
      length: '38m',
      type: 'Sailing Yacht',
      flag: '🇫🇷',
      status: 'Disponible',
      guests: 6,
      cabins: 3,
      crew: 5,
      speed: '14kn',
      pricing: {
        high: '€180,000',
        low: '€140,000'
      },
      metrics: {
        charter: '82%',
        rating: '4.9',
        revenue: '€2.9M'
      },
      location: 'Saint-Tropez',
      image: 'mistral',
      source: 'Northrop & Johnson'
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'Disponible': return 'bg-green-100 text-green-800';
      case 'En Charter': return 'bg-red-100 text-red-800';
      case 'Maintenance': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getYachtImage = (imageName) => {
    // Generate SVG placeholder for yacht images
    const colors = {
      serenity: { bg: '#1e3a5f', accent: '#c9a961' },
      windspirit: { bg: '#4a90a4', accent: '#ffffff' },
      oceandream: { bg: '#2c5282', accent: '#c9a961' },
      azurewave: { bg: '#1a365d', accent: '#4a90a4' },
      goldenstar: { bg: '#d69e2e', accent: '#ffffff' },
      mistral: { bg: '#2d3748', accent: '#81e6d9' }
    };
    
    const color = colors[imageName] || colors.serenity;
    
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 450 280'%3E%3Cdefs%3E%3ClinearGradient id='${imageName}' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:${encodeURIComponent(color.bg)};stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:${encodeURIComponent(color.accent)};stop-opacity:0.7' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='450' height='280' fill='url(%23${imageName})' /%3E%3Cpath d='M 50 180 Q 225 150 400 180 L 400 280 L 50 280 Z' fill='${encodeURIComponent(color.accent)}' opacity='0.3'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='white' font-size='18' font-family='Arial'%3EYacht Image%3C/text%3E%3C/svg%3E`;
  };

  const getSourceColor = (source) => {
    switch(source) {
      case 'Direct': return 'bg-green-100 text-green-800 border-green-200';
      case 'Yacht Charter Index': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Yacht Portfolio': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Fraser Yachts': return 'bg-red-100 text-red-800 border-red-200';
      case 'Burgess': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Northrop & Johnson': return 'bg-teal-100 text-teal-800 border-teal-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-700 to-blue-600 text-white p-8 relative overflow-hidden">
        <div className="absolute top-0 right-12 text-8xl opacity-5">
          <Anchor className="w-48 h-48 transform -rotate-12" />
        </div>
        <h1 className="text-4xl font-light tracking-widest mb-2 relative z-10">PORTFOLIO YACHTS</h1>
        <p className="text-sm tracking-wider opacity-90 relative z-10">Gestion complète de votre flotte de yachts et superyachts de luxe</p>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-yellow-600"></div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white p-6 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">89</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Yachts Actifs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">€275K</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Charter Moyen/Sem</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">72%</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Taux Charter</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">42m</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Longueur Moyenne</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">€28M</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Revenue YTD</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-blue-700">12</div>
            <div className="text-xs uppercase tracking-wide text-gray-500">En Navigation</div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-6 border-t shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Type de Yacht</label>
            <Select>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tous types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="motor">Motor Yacht</SelectItem>
                <SelectItem value="sailing">Sailing Yacht</SelectItem>
                <SelectItem value="catamaran">Catamaran</SelectItem>
                <SelectItem value="super">Superyacht (30m+)</SelectItem>
                <SelectItem value="mega">Megayacht (50m+)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Zone de Navigation</label>
            <Select>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Toutes zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes zones</SelectItem>
                <SelectItem value="med">Méditerranée</SelectItem>
                <SelectItem value="caribbean">Caraïbes</SelectItem>
                <SelectItem value="cote">Côte d'Azur</SelectItem>
                <SelectItem value="greece">Îles Grecques</SelectItem>
                <SelectItem value="balearic">Baléares</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Longueur</label>
            <Select>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="small">&lt; 30m</SelectItem>
                <SelectItem value="medium">30-50m</SelectItem>
                <SelectItem value="large">50-80m</SelectItem>
                <SelectItem value="mega">80m+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Capacité</label>
            <Select>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="small">2-8 invités</SelectItem>
                <SelectItem value="medium">8-12 invités</SelectItem>
                <SelectItem value="large">12-20 invités</SelectItem>
                <SelectItem value="xl">20+ invités</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-80">
            <label className="text-xs uppercase tracking-wide text-gray-500">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input 
                placeholder="Nom, IMO, pavillon..." 
                className="pl-10"
              />
            </div>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter Yacht
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Ajouter un nouveau yacht</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Nom du yacht</label>
                    <Input placeholder="Ex: M/Y Serenity" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Constructeur</label>
                    <Input placeholder="Ex: Benetti" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">Année</label>
                    <Input placeholder="2024" type="number" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Longueur</label>
                    <Input placeholder="75m" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Type</label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Type de yacht" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="motor">Motor Yacht</SelectItem>
                        <SelectItem value="sailing">Sailing Yacht</SelectItem>
                        <SelectItem value="catamaran">Catamaran</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium">Invités</label>
                    <Input placeholder="12" type="number" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Cabines</label>
                    <Input placeholder="6" type="number" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Équipage</label>
                    <Input placeholder="18" type="number" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Vitesse</label>
                    <Input placeholder="15kn" />
                  </div>
                </div>
                <Button className="w-full bg-blue-700 hover:bg-blue-800 text-white">
                  Ajouter au portfolio
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="flex gap-2">
            <Button
              variant={activeView === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('grid')}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={activeView === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={activeView === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('map')}
            >
              <Map className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Grid View - 3 colonnes */}
        {activeView === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {yachtsData.map((yacht) => (
              <Card key={yacht.id} className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="relative h-48">
                  <img 
                    src={getYachtImage(yacht.image)} 
                    alt={yacht.name}
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-3 left-3 bg-blue-900 text-white text-xs">
                    {yacht.type}
                  </Badge>
                  <span className="absolute top-3 right-3 text-xl">{yacht.flag}</span>
                  <Badge className={`absolute bottom-3 left-3 text-xs ${getSourceColor(yacht.source)}`}>
                    {yacht.source}
                  </Badge>
                  <Badge className={`absolute bottom-3 right-3 text-xs ${getStatusColor(yacht.status)}`}>
                    {yacht.status}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold">{yacht.name}</h3>
                      <p className="text-sm text-gray-600">{yacht.builder} • {yacht.year}</p>
                    </div>
                    <div className="text-lg font-semibold text-blue-700">{yacht.length}</div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 py-3 border-t border-b mb-3">
                    <div className="text-center">
                      <Users className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold text-sm">{yacht.guests}</div>
                      <div className="text-xs text-gray-500">Invités</div>
                    </div>
                    <div className="text-center">
                      <Bed className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold text-sm">{yacht.cabins}</div>
                      <div className="text-xs text-gray-500">Cabines</div>
                    </div>
                    <div className="text-center">
                      <Anchor className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold text-sm">{yacht.crew}</div>
                      <div className="text-xs text-gray-500">Équipage</div>
                    </div>
                    <div className="text-center">
                      <Zap className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                      <div className="font-semibold text-sm">{yacht.speed}</div>
                      <div className="text-xs text-gray-500">Vitesse</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gray-50 p-2 rounded">
                      <div className="text-xs text-gray-500 mb-1">Haute Saison</div>
                      <div className="text-sm font-light text-blue-700">{yacht.pricing.high}</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <div className="text-xs text-gray-500 mb-1">Basse Saison</div>
                      <div className="text-sm font-light text-blue-700">{yacht.pricing.low}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 py-3 border-t text-center">
                    <div>
                      <div className="font-semibold text-sm">{yacht.metrics.charter}</div>
                      <div className="text-xs text-gray-500">Charter</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{yacht.metrics.rating}</div>
                      <div className="text-xs text-gray-500">Note</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{yacht.metrics.revenue}</div>
                      <div className="text-xs text-gray-500">Revenue</div>
                    </div>
                  </div>

                  <div className="flex gap-1 mt-3">
                    <Button variant="outline" size="sm" className="flex-1 text-xs">
                      Détails
                    </Button>
                    <Button size="sm" className="flex-1 bg-blue-700 hover:bg-blue-800 text-white text-xs">
                      Charter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* List View */}
        {activeView === 'list' && (
          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 font-semibold">
                <div className="col-span-1">Photo</div>
                <div className="col-span-2">Yacht</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-1">Longueur</div>
                <div className="col-span-1">Invités</div>
                <div className="col-span-1">Location</div>
                <div className="col-span-1">Source</div>
                <div className="col-span-2">Tarifs</div>
                <div className="col-span-1">Charter</div>
                <div className="col-span-1">Status</div>
              </div>
              {yachtsData.map((yacht) => (
                <div key={yacht.id} className="grid grid-cols-12 gap-4 p-4 border-b hover:bg-gray-50 transition-colors items-center">
                  <div className="col-span-1">
                    <img 
                      src={getYachtImage(yacht.image)} 
                      alt={yacht.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="font-semibold">{yacht.name}</div>
                    <div className="text-sm text-gray-600">{yacht.builder} {yacht.year}</div>
                  </div>
                  <div className="col-span-1 text-sm">{yacht.type.split(' ')[0]}</div>
                  <div className="col-span-1 font-semibold text-blue-700">{yacht.length}</div>
                  <div className="col-span-1">{yacht.guests}</div>
                  <div className="col-span-1 text-sm">{yacht.location}</div>
                  <div className="col-span-1">
                    <Badge className={`text-xs ${getSourceColor(yacht.source)}`}>
                      {yacht.source}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-sm">
                    <div>{yacht.pricing.high} / {yacht.pricing.low}</div>
                  </div>
                  <div className="col-span-1 font-semibold">{yacht.metrics.charter}</div>
                  <div className="col-span-1">
                    <Badge className={getStatusColor(yacht.status)}>
                      {yacht.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Map View */}
        {activeView === 'map' && (
          <Card>
            <CardContent className="p-6">
              <div className="h-96 bg-blue-50 rounded-lg flex items-center justify-center relative">
                <div className="text-center">
                  <Map className="w-16 h-16 mx-auto mb-4 text-blue-300" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Carte Interactive des Yachts</h3>
                  <p className="text-gray-500">Visualisation géographique de votre flotte en temps réel</p>
                </div>
                
                {/* Map Legend */}
                <div className="absolute top-4 left-4 bg-white p-4 rounded-lg shadow-lg max-w-xs">
                  <h4 className="font-semibold mb-3">Légende</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm">Disponible</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm">En Charter</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm">Maintenance</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default YachtsManagement;